import { ANNOTATION_TOOLS } from "./constants";
import {
  AddRounded as AddIcon,
  SendRounded as SubmitIcon,
  PriorityHighRounded as PriorityIcon,
  VisibilityRounded as ConditionIcon,
  DeleteRounded as ClearIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Button from "components/common/Button";
import { useEffect, useState } from "react";

export const CommentDialog = ({ open, onClose, onSubmit }) => {
  const [text, setText] = useState("");

  const handleSubmit = () => {
    if (text.trim()) {
      onSubmit(text.trim());
      setText("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={"xs"} fullWidth>
      <DialogTitle>{"Add Comment"}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          placeholder={"Enter your comment or note..."}
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color={"inherit"}>
          {"Cancel"}
        </Button>
        <Button onClick={handleSubmit} isDisabled={!text.trim()}>
          {"Add Comment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const NewVariableDialog = ({ open, onClose, onSubmit }) => {
  const [fieldName, setFieldName] = useState("");
  const [description, setDescription] = useState("");
  const [dataSource, setDataSource] = useState("");
  const [dataType, setDataType] = useState("string");
  const [captureMethod, setCaptureMethod] = useState("");

  const handleSubmit = () => {
    onSubmit({
      fieldName,
      description,
      dataSource,
      dataType,
      captureMethod,
    });
    setFieldName("");
    setDescription("");
    setDataSource("");
    setDataType("string");
    setCaptureMethod("");
    onClose();
  };

  const isValid = fieldName.trim() && description.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
      <DialogTitle>{"Create Custom Data Field"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Alert severity={"info"} icon={<AddIcon />}>
            {
              "Create a custom field to place on your document. Describe what data it should contain and we'll set it up for you."
            }
          </Alert>

          <TextField
            fullWidth
            label={"Field Name"}
            placeholder={"e.g., Broker License Number, Surplus Lines Tax Rate"}
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            helperText={"A short, descriptive name for this field"}
            required
          />

          <TextField
            fullWidth
            select
            label={"Data Type"}
            value={dataType}
            onChange={(e) => setDataType(e.target.value)}
            SelectProps={{ native: true }}
            helperText={"What type of data will this field contain?"}
          >
            <option value={"string"}>
              {"Text (names, descriptions, etc.)"}
            </option>
            <option value={"number"}>
              {"Number (amounts, percentages, counts)"}
            </option>
            <option value={"date"}>
              {"Date (effective dates, deadlines, etc.)"}
            </option>
          </TextField>

          <TextField
            fullWidth
            multiline
            minRows={2}
            label={"Description"}
            placeholder={
              "What should this field display? Be specific about the format and content expected..."
            }
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            helperText={"Describe what this field should show in the document"}
            required
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            label={"Data Source"}
            placeholder={
              "Where does this data come from? (e.g., broker profile, submission form, calculated from premium, manual entry)"
            }
            value={dataSource}
            onChange={(e) => setDataSource(e.target.value)}
            helperText={"Where should the system get this data?"}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            label={"How should it be captured?"}
            placeholder={
              "e.g., Text input field, dropdown selection from list, date picker, calculated automatically from other fields..."
            }
            value={captureMethod}
            onChange={(e) => setCaptureMethod(e.target.value)}
            helperText={"How should users enter or select this data?"}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color={"inherit"}>
          {"Cancel"}
        </Button>
        <Button
          onClick={handleSubmit}
          isDisabled={!isValid}
          startIcon={<AddIcon />}
        >
          {"Create Field"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const SubmitRequestDialog = ({
  open,
  onClose,
  onSubmit,
  fileName,
  file,
  s3FileId,
  placements,
  customFields,
  requestType = "NEW_TEMPLATE",
  annotations = [],
  allowEmpty = false,
  fileReplaced = false,
  initialMetadata = null,
}) => {
  const theme = useTheme();
  const [title, setTitle] = useState(initialMetadata?.title ?? "");
  const [description, setDescription] = useState(
    initialMetadata?.description ?? ""
  );
  const [priority, setPriority] = useState(
    initialMetadata?.priority ?? "MEDIUM"
  );

  const isNewTemplate = requestType === "NEW_TEMPLATE";

  const handleSubmit = async () => {
    onSubmit({
      title:
        title ||
        `${isNewTemplate ? "New Template" : "Template Edit"}: ${fileName}`,
      description:
        description ||
        `${
          isNewTemplate
            ? "New template request"
            : "Template modification request"
        } with ${placements.length} field placements and ${
          annotations.length
        } annotations.`,
      priority,
      fileName,
      s3FileId,
      fileType:
        file?.type ||
        (fileName?.endsWith(".pdf")
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document"),
      placements,
      customFields,
      annotations,
    });
    setTitle("");
    setDescription("");
    setPriority("MEDIUM");
    onClose();
  };

  const conditionalFields = placements.filter((p) => p.condition);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={"md"} fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <SubmitIcon color={isNewTemplate ? "primary" : "secondary"} />
          <Typography variant={"h6"}>
            {isNewTemplate
              ? "Submit New Template Request"
              : "Submit Template Modification Request"}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Paper
            variant={"outlined"}
            sx={{
              p: 2,
              backgroundColor: `${theme.palette.success.main}08`,
              borderColor: `${theme.palette.success.main}40`,
            }}
          >
            <Typography
              variant={"subtitle2"}
              sx={{ fontWeight: 600, mb: 1.5, color: "success.dark" }}
            >
              {"Request Summary"}
            </Typography>
            <Stack direction={"row"} spacing={4} flexWrap={"wrap"} useFlexGap>
              <Box>
                <Typography
                  variant={"caption"}
                  color={"text.secondary"}
                  display={"block"}
                >
                  {"Document"}
                </Typography>
                <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
                  {fileName}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant={"caption"}
                  color={"text.secondary"}
                  display={"block"}
                >
                  {"Field Placements"}
                </Typography>
                <Typography variant={"body2"} sx={{ fontWeight: 500 }}>
                  {placements.length}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant={"caption"}
                  color={"text.secondary"}
                  display={"block"}
                >
                  {"Conditional Fields"}
                </Typography>
                <Typography
                  variant={"body2"}
                  sx={{ fontWeight: 500, color: "info.main" }}
                >
                  {conditionalFields.length}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {placements.length > 0 && (
            <Box>
              <Typography variant={"subtitle2"} sx={{ fontWeight: 600, mb: 1 }}>
                {"Fields to be Added"}
              </Typography>
              <Stack direction={"row"} flexWrap={"wrap"} gap={0.5}>
                {placements.map((p) => (
                  <Chip
                    key={p.id}
                    label={
                      p.condition
                        ? `${p.variable.label} (conditional)`
                        : p.variable.label
                    }
                    size={"small"}
                    color={
                      p.variable.isCustom
                        ? "warning"
                        : p.condition
                        ? "info"
                        : "default"
                    }
                    variant={"outlined"}
                    sx={{
                      fontSize: "0.75rem",
                      fontStyle: p.condition ? "italic" : "normal",
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {annotations.length > 0 && (
            <Box>
              <Typography variant={"subtitle2"} sx={{ fontWeight: 600, mb: 1 }}>
                {"Annotations"}
              </Typography>
              <Stack direction={"row"} flexWrap={"wrap"} gap={0.5}>
                {annotations.map((a) => {
                  const tool = ANNOTATION_TOOLS.find((t) => t.id === a.type);
                  const fullLabel =
                    a.type === "comment" ? `"${a.text}"` : tool?.label;
                  const truncatedLabel =
                    a.type === "comment"
                      ? `"${a.text?.substring(0, 15)}${
                          a.text?.length > 15 ? "..." : ""
                        }"`
                      : tool?.label;
                  return (
                    <Tooltip
                      key={a.id}
                      title={fullLabel}
                      arrow
                      placement={"top"}
                    >
                      <Chip
                        label={truncatedLabel}
                        size={"small"}
                        variant={"outlined"}
                        sx={{
                          fontSize: "0.75rem",
                          borderColor: tool?.color,
                          color: tool?.color,
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
            </Box>
          )}

          <Divider />

          <TextField
            fullWidth
            label={"Template Name"}
            placeholder={fileName}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            helperText={"What this template should be called once published"}
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label={"Any additional comments"}
            placeholder={
              isNewTemplate
                ? "Anything else we should know about this new template..."
                : "Anything else we should know about the changes..."
            }
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <TextField
            fullWidth
            select
            label={"Priority"}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            SelectProps={{ native: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position={"start"}>
                  <PriorityIcon
                    sx={{
                      color:
                        priority === "HIGH"
                          ? "error.main"
                          : priority === "MEDIUM"
                          ? "warning.main"
                          : "success.main",
                    }}
                  />
                </InputAdornment>
              ),
            }}
          >
            <option value={"LOW"}>
              {"Low - No rush, whenever convenient"}
            </option>
            <option value={"MEDIUM"}>{"Medium - Standard turnaround"}</option>
            <option value={"HIGH"}>{"High - Urgent, needed soon"}</option>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color={"inherit"}>
          {"Cancel"}
        </Button>
        <Button
          onClick={handleSubmit}
          startIcon={<SubmitIcon />}
          isDisabled={
            !allowEmpty &&
            !fileReplaced &&
            placements.length === 0 &&
            annotations.length === 0
          }
        >
          {"Submit Request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Common condition options based on frequently used template logic
const COMMON_CONDITIONS = [
  { value: "", label: "Always show (no condition)" },
  { value: "L.triaStatus == 'INCLUDED'", label: "TRIA is Included" },
  { value: "L.triaStatus == 'GROSS_UP'", label: "TRIA is Gross Up" },
  { value: "L.triaStatus == 'NOT_INCLUDED'", label: "TRIA is Not Included" },
  { value: "person(S, 'SURPLUS_BROKER')", label: "Surplus Broker exists" },
  { value: "person(S, 'PUNI_BROKER')", label: "PUNI Broker exists" },
  { value: "L.paperProvider == 'TRM_ADMITTED'", label: "Paper is Admitted" },
  {
    value: "L.paperProvider != 'TRM_ADMITTED'",
    label: "Paper is Non-Admitted",
  },
  {
    value: "S.program.industryClass == 'CONSTRUCTION_PROJECT'",
    label: "Is Construction Project",
  },
  { value: "L.minimumEarnedPremium", label: "Has Minimum Earned Premium" },
  { value: "L.paymentTerms", label: "Has Payment Terms" },
  { value: "S.program.priorSubmission", label: "Has Prior Submission" },
  { value: "custom", label: "Custom condition..." },
];

export const ConditionDialog = ({ open, onClose, onSubmit, placement }) => {
  const theme = useTheme();
  const [selectedCondition, setSelectedCondition] = useState("");
  const [customCondition, setCustomCondition] = useState("");
  const [isCustom, setIsCustom] = useState(false);

  useEffect(() => {
    if (open && placement) {
      const existingCondition = placement.condition || "";
      const matchingOption = COMMON_CONDITIONS.find(
        (c) => c.value === existingCondition
      );
      if (matchingOption && matchingOption.value !== "custom") {
        setSelectedCondition(existingCondition);
        setIsCustom(false);
        setCustomCondition("");
      } else if (existingCondition) {
        setSelectedCondition("custom");
        setIsCustom(true);
        setCustomCondition(existingCondition);
      } else {
        setSelectedCondition("");
        setIsCustom(false);
        setCustomCondition("");
      }
    }
  }, [open, placement]);

  const handleConditionChange = (value) => {
    setSelectedCondition(value);
    if (value === "custom") {
      setIsCustom(true);
    } else {
      setIsCustom(false);
      setCustomCondition("");
    }
  };

  const handleSubmit = () => {
    const finalCondition = isCustom
      ? customCondition.trim()
      : selectedCondition;
    onSubmit(placement.id, finalCondition || null);
    onClose();
  };

  const handleClear = () => {
    onSubmit(placement.id, null);
    onClose();
  };

  if (!placement) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <ConditionIcon color={"info"} />
          <Typography variant={"h6"}>{"Set Display Condition"}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Paper
            variant={"outlined"}
            sx={{
              p: 2,
              backgroundColor: `${theme.palette.info.main}08`,
              borderColor: `${theme.palette.info.main}40`,
            }}
          >
            <Typography variant={"subtitle2"} sx={{ fontWeight: 600, mb: 0.5 }}>
              {"Field: "}
              {placement.variable.label}
            </Typography>
            <Typography variant={"caption"} color={"text.secondary"}>
              {
                "Specify when this field should appear in the document. Leave empty to always show."
              }
            </Typography>
          </Paper>

          <FormControl fullWidth>
            <InputLabel>{"Show this field when..."}</InputLabel>
            <Select
              value={selectedCondition}
              onChange={(e) => handleConditionChange(e.target.value)}
              label={"Show this field when..."}
            >
              {COMMON_CONDITIONS.map((condition) => (
                <MenuItem
                  key={condition.value || "empty"}
                  value={condition.value}
                >
                  {condition.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isCustom && (
            <TextField
              fullWidth
              label={"Custom Condition"}
              placeholder={
                "e.g., L.shareOfLine > 50 or S.program.currency == 'USD'"
              }
              value={customCondition}
              onChange={(e) => setCustomCondition(e.target.value)}
              helperText={"Enter a Jinja-style condition expression"}
              InputProps={{
                sx: { fontFamily: "monospace", fontSize: "0.875rem" },
              }}
            />
          )}

          <Alert
            severity={"info"}
            sx={{ "& .MuiAlert-message": { fontSize: "0.8rem" } }}
          >
            {"This tells us when to include the field. Use expressions like "}
            <code>{"L.triaStatus == 'INCLUDED'"}</code>
            {" or "}
            <code>{"person(S, 'BROKER')"}</code>
            {" to check if something exists."}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {placement.condition && (
          <Button
            onClick={handleClear}
            color={"error"}
            startIcon={<ClearIcon />}
            sx={{ mr: "auto" }}
          >
            {"Remove Condition"}
          </Button>
        )}
        <Button onClick={onClose} color={"inherit"}>
          {"Cancel"}
        </Button>
        <Button onClick={handleSubmit} startIcon={<ConditionIcon />}>
          {"Save Condition"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
